import { KnownUploadImageDtoCategory } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Button } from '@mui/material';
import { ChangeEvent, ReactNode, useState } from 'react';
import { useMutationUploadImage } from '../../../../features/settings/settings.queries';

const MAX_FILE_SIZE_BYTES = 500 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/svg+xml'];

/** Small enough to sit beside the button; the image keeps its own ratio inside it. */
const PREVIEW_STYLE = { maxWidth: 200, maxHeight: 35, objectFit: 'contain', display: 'block' } as const;

export interface ImageUploadProps {
  /** Ties the hidden input to its label, so it must be unique on the page. */
  id: string;
  /** Public uploads carry no SAS token, so the URL never expires. */
  isPublic?: boolean;
  category?: KnownUploadImageDtoCategory;
  onUploaded(url: string): void;
  disabled?: boolean;
  children?: ReactNode;
  /** The current image's full URL, shown as a thumbnail when `showPreview` is on. */
  value?: string;
  showPreview?: boolean;
}

/** Uploads a file and hands back its URL — what every image field on an entity actually stores. */
export function ImageUpload({
  id,
  isPublic,
  category,
  onUploaded,
  disabled,
  children,
  value,
  showPreview = false
}: ImageUploadProps) {
  const mutation = useMutationUploadImage();
  const [error, setError] = useState<string | null>(null);

  function upload(file: File) {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      mutation.mutate(
        {
          // The endpoint wants the payload alone, without the data-URI prefix.
          imageBase64Data: (reader.result as string).split(',')[1],
          imageFileName: file.name,
          isPublic,
          category
        },
        {
          onSuccess: (data) => onUploaded(data.url),
          onError: (uploadError) => setError(uploadError.message)
        }
      );
    };
    reader.onerror = () => setError('Error reading file');
  }

  function onSelect(event: ChangeEvent<HTMLInputElement>) {
    setError(null);
    const files = event.target.files;

    if (!files?.length) {
      return;
    }
    if (files.length !== 1) {
      setError('Please select only one file');
      return;
    }
    if (files[0].size > MAX_FILE_SIZE_BYTES) {
      setError('File size exceeds 500 KB');
      return;
    }
    if (!ACCEPTED_TYPES.includes(files[0].type)) {
      setError('Invalid file type. Only JPEG, PNG, HEIC, and SVG are supported');
      return;
    }

    upload(files[0]);
  }

  return (
    <div>
      <input type="file" accept=".jpeg,.jpg,.png,.heic,.svg" style={{ display: 'none' }} id={id} onChange={onSelect} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {showPreview && value && <img src={value} alt="" style={PREVIEW_STYLE} />}
        {/* Flex, not inline: an inline label keeps a line box that pushes the button off centre. */}
        <label htmlFor={id} style={{ display: 'flex' }}>
          <Button variant="outlined" color="secondary" component="span" disabled={disabled || mutation.isLoading}>
            {mutation.isLoading ? 'Uploading...' : children || 'Upload'}
          </Button>
        </label>
      </div>
      {error && <div style={{ color: 'red', marginTop: '8px' }}>{error}</div>}
    </div>
  );
}
